import React, { useState, Children } from 'react';
import { IconChevronRight, IconChevronDown, IconPlus, IconClock, IconAlertTriangle, IconStethoscope, IconSchool, IconHome, IconShoppingCart, IconArrowLeft } from '@tabler/icons-react';
interface FamigliaPageProps {
  onOpenChild: (childId: string, childName?: string) => void;
  onOpenActivities: () => void;
  onOpenCaregiver: (caregiverId: string, caregiverName?: string) => void;
  onOpenStats?: () => void;
}
// Enhanced Category Data with Caregiver Distribution
const mockCategories = [{
  id: 'health',
  name: 'Salute',
  icon: IconStethoscope,
  color: '#B0EC9C',
  totalTasks: 8,
  completedTasks: 5,
  caregiverDistribution: [{
    id: '1',
    percentage: 45
  }, {
    id: '2',
    percentage: 30
  }, {
    id: '3',
    percentage: 20
  }, {
    id: '5',
    percentage: 5
  } // Anna
  ]
}, {
  id: 'school',
  name: 'Scuola',
  icon: IconSchool,
  color: '#AEDAFF',
  totalTasks: 12,
  completedTasks: 8,
  caregiverDistribution: [{
    id: '1',
    percentage: 50
  }, {
    id: '2',
    percentage: 35
  }, {
    id: '3',
    percentage: 10
  }, {
    id: '5',
    percentage: 5
  } // Anna
  ]
}, {
  id: 'home',
  name: 'Casa',
  icon: IconHome,
  color: '#FAD88A',
  totalTasks: 15,
  completedTasks: 10,
  caregiverDistribution: [{
    id: '2',
    percentage: 60
  }, {
    id: '1',
    percentage: 20
  }, {
    id: '5',
    percentage: 20
  } // Anna
  ]
}, {
  id: 'shopping',
  name: 'Spesa',
  icon: IconShoppingCart,
  color: '#DDD6FE',
  totalTasks: 5,
  completedTasks: 2,
  caregiverDistribution: [{
    id: '1',
    percentage: 70
  }, {
    id: '2',
    percentage: 30
  } // Luca
  ]
}];
// Caregivers Data (Centralized for lookup)
const mockCaregivers = [{
  id: '1',
  name: 'Maria',
  role: 'Mamma',
  avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face'
}, {
  id: '2',
  name: 'Luca',
  role: 'Papà',
  avatar: 'https://cdn.magicpatterns.com/uploads/7G4z2y5zxpC8H8Kk9BNvv6/felipe-salgado-zssU4tUQ-18-unsplash.jpg'
}, {
  id: '3',
  name: 'Nonna Rosa',
  role: 'Nonna',
  avatar: 'https://images.unsplash.com/photo-1566616213894-2d4e1baee5d8?w=150&h=150&fit=crop&crop=face'
}, {
  id: '4',
  name: 'Zio Marco',
  role: 'Zio',
  avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'
}, {
  id: '5',
  name: 'Anna',
  role: 'Baby Sitter',
  avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face'
}];
// Children Data
const mockChildren = [{
  id: '1',
  name: 'Sofia',
  age: 6,
  avatar: 'https://cdn.magicpatterns.com/uploads/c3jxasi4fCCZ93c9aemubP/adele-shafiee-KCvjle4_4ps-unsplash.jpg',
  infoCount: 24
}, {
  id: '2',
  name: 'Marco',
  age: 4,
  avatar: 'https://cdn.magicpatterns.com/uploads/1EXQ4FkeqG6UuqJKWikck9/tatev-arsh-ZtB5Haz58os-unsplash.jpg',
  infoCount: 18
}];
// Activities Data
const mockActivities = {
  pending: 5,
  completed: 12,
  upcoming: [{
    id: '1',
    title: 'Vaccino Sofia',
    date: 'Giovedì',
    urgent: false,
    assigneeId: '1' // Maria - Mamma
  }, {
    id: '2',
    title: 'Rinnovo passaporto',
    date: 'Scade tra 45gg',
    urgent: true,
    assigneeId: '2' // Luca - Papà
  }]
};
export function FamigliaPage({
  onOpenChild,
  onOpenActivities,
  onOpenCaregiver,
  onOpenStats
}: FamigliaPageProps) {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [isCategoriesExpanded, setIsCategoriesExpanded] = useState(false);
  const selectedCategory = mockCategories.find(c => c.id === selectedCategoryId);
  // Calculate overall totals
  const totalTasks = mockCategories.reduce((sum, c) => sum + c.totalTasks, 0);
  const totalCompleted = mockCategories.reduce((sum, c) => sum + c.completedTasks, 0);
  const overallPercentage = Math.round(totalCompleted / totalTasks * 100);
  // Helper to get caregiver details
  const getCaregiver = (id: string) => mockCaregivers.find(c => c.id === id);
  // Compute overall collaboration percentage for each caregiver
  const caregiverCollaboration = mockCaregivers.map(caregiver => {
    let weightedSum = 0;
    let totalWeight = 0;
    mockCategories.forEach(category => {
      const dist = category.caregiverDistribution.find(d => d.id === caregiver.id);
      if (dist) {
        weightedSum += dist.percentage * category.totalTasks;
        totalWeight += category.totalTasks;
      }
    });
    return {
      ...caregiver,
      percentage: totalWeight > 0 ? Math.round(weightedSum / totalWeight) : 0
    };
  }).sort((a, b) => b.percentage - a.percentage);
  return <div className="flex-1 overflow-y-auto" data-id="element-715">
      <div className="px-4 py-4 space-y-5" data-id="element-716">
        {/* Header / Navigation */}
        {selectedCategory ? <div className="flex items-center gap-3 mb-2" data-id="element-717">
            <button onClick={() => setSelectedCategoryId(null)} className="flex items-center gap-1 py-2 pr-2 transition-opacity hover:opacity-70" aria-label="Torna a Famiglia" data-id="element-718">
              <IconArrowLeft className="w-5 h-5 text-yeppy-text" stroke={2} data-id="element-719" />
              <span className="font-poppins text-base text-yeppy-text font-medium" data-id="element-720">
                Famiglia
              </span>
            </button>
            <div className="flex-1 flex justify-end" data-id="element-721">
              <div className="text-right" data-id="element-722">
                <h1 className="font-rubik font-bold text-base text-yeppy-text" data-id="element-723">
                  {selectedCategory.name}
                </h1>
                <p className="font-poppins text-xs text-yeppy-text text-opacity-60" data-id="element-724">
                  Contributo per caregiver
                </p>
              </div>
            </div>
          </div> : null}

        {/* Main Content Area */}
        {selectedCategory ?
      // DETAIL VIEW: Caregiver Distribution for Selected Category
      <div className="space-y-3 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-725">
            {selectedCategory.caregiverDistribution.map(item => {
          const caregiver = getCaregiver(item.id);
          if (!caregiver) return null;
          return <button key={item.id} onClick={() => onOpenCaregiver(caregiver.id, caregiver.name)} className="w-full bg-white rounded-2xl p-4 border border-yeppy-text border-opacity-10 flex items-center gap-3 text-left transition-all" data-id="element-726">
                  <img src={caregiver.avatar} alt={caregiver.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0 border border-yeppy-text border-opacity-10" data-id="element-727" />
                  <div className="flex-1 min-w-0" data-id="element-728">
                    <div className="flex items-center justify-between mb-1" data-id="element-729">
                      <div data-id="element-730">
                        <span className="font-poppins text-sm font-medium text-yeppy-text" data-id="element-731">
                          {caregiver.name}
                        </span>
                        <span className="font-poppins text-xs text-yeppy-text text-opacity-40 ml-1.5" data-id="element-732">
                          {caregiver.role}
                        </span>
                      </div>
                      <span className="font-rubik font-medium text-sm text-yeppy-text" data-id="element-733">
                        {item.percentage}%
                      </span>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full h-2 bg-yeppy-bg rounded-full overflow-hidden" data-id="element-734">
                      <div className="h-full rounded-full transition-all duration-500" style={{
                  width: `${item.percentage}%`,
                  backgroundColor: selectedCategory.color
                }} data-id="element-735" />
                    </div>
                  </div>
                  <IconChevronRight className="w-4 h-4 text-yeppy-text text-opacity-30" stroke={1.5} data-id="element-736" />
                </button>;
        })}
          </div> :
      // OVERVIEW VIEW
      <div className="space-y-5 animate-in fade-in slide-in-from-left-4 duration-300" data-id="element-737">
            {/* Expandable Categories Card - MOVED TO TOP */}
            <div data-id="element-738">
              <div className="flex items-center justify-between mb-3" data-id="element-739">
                <h2 className="font-rubik font-semibold text-xs uppercase tracking-wider text-yeppy-text text-opacity-40" data-id="element-740">
                  Attività
                </h2>
                <button onClick={onOpenActivities} className="font-poppins text-sm text-yeppy-text text-opacity-60 flex items-center gap-1" data-id="element-741">
                  Vedi tutte
                  <IconChevronRight className="w-4 h-4" stroke={1.5} data-id="element-742" />
                </button>
              </div>

              <div className="bg-white border border-yeppy-text border-opacity-10 rounded-2xl overflow-hidden" data-id="element-743">
                {/* Summary Header - always visible */}
                <button onClick={() => setIsCategoriesExpanded(!isCategoriesExpanded)} className="w-full p-4 text-left" data-id="element-744">
                  <div className="flex items-center justify-between mb-1" data-id="element-745">
                    <div className="flex items-center gap-2" data-id="element-746">
                      <span className="font-rubik font-semibold text-sm text-yeppy-text" data-id="element-747">
                        {totalCompleted}/{totalTasks}
                      </span>
                      <span className="font-poppins text-xs text-yeppy-text text-opacity-50" data-id="element-748">
                        completate
                      </span>
                    </div>
                    <IconChevronDown className={`w-4 h-4 text-yeppy-text text-opacity-40 transition-transform duration-300 ${isCategoriesExpanded ? 'rotate-180' : ''}`} stroke={1.5} data-id="element-749" />
                  </div>

                  {/* Segmented Progress Bar */}
                  <div className="w-full h-2 bg-yeppy-bg rounded-full overflow-hidden" data-id="element-750">
                    <div className="h-full rounded-full transition-all duration-500" style={{
                  width: `${overallPercentage}%`,
                  backgroundColor: '#1D2A36'
                }} data-id="element-751" />
                  </div>
                </button>

                {/* Expanded: Category Breakdown */}
                <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isCategoriesExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-752">
                  <div className="border-t border-yeppy-text border-opacity-10" data-id="element-753">
                    {mockCategories.map((category, index) => {
                  const Icon = category.icon;
                  const percentage = Math.round(category.completedTasks / category.totalTasks * 100);
                  return <button key={category.id} onClick={e => {
                    e.stopPropagation();
                    setSelectedCategoryId(category.id);
                  }} className={`w-full px-4 py-3 flex items-center gap-3 text-left transition-colors ${index > 0 ? 'border-t border-yeppy-text border-opacity-10' : ''}`} data-id="element-754">
                          {/* Category Icon */}
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                      backgroundColor: `${category.color}33`
                    }} data-id="element-755">
                            <Icon className="w-4.5 h-4.5" stroke={2.5} style={{
                        color: category.color
                      }} data-id="element-756" />
                          </div>

                          {/* Category Info + Progress */}
                          <div className="flex-1 min-w-0" data-id="element-757">
                            <div className="flex items-center justify-between mb-1" data-id="element-758">
                              <span className="font-poppins text-sm font-medium text-yeppy-text" data-id="element-759">
                                {category.name}
                              </span>
                              <span className="font-poppins text-xs text-yeppy-text text-opacity-50" data-id="element-760">
                                {category.completedTasks}/{category.totalTasks}
                              </span>
                            </div>
                            <div className="w-full h-1.5 bg-yeppy-bg rounded-full overflow-hidden" data-id="element-761">
                              <div className="h-full rounded-full transition-all duration-300" style={{
                          width: `${percentage}%`,
                          backgroundColor: category.color
                        }} data-id="element-762" />
                            </div>
                          </div>

                          <IconChevronRight className="w-4 h-4 text-yeppy-text text-opacity-30 flex-shrink-0" stroke={1.5} data-id="element-763" />
                        </button>;
                })}
                  </div>
                </div>
              </div>
            </div>

            {/* Children Section */}
            <div data-id="element-764">
              <div className="flex items-center justify-between mb-3" data-id="element-765">
                <h2 className="font-rubik font-semibold text-xs uppercase tracking-wider text-yeppy-text text-opacity-40" data-id="element-766">
                  Figli
                </h2>
                <button className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-yeppy-text border-opacity-15 text-yeppy-text text-opacity-60 active:scale-95 transition-transform" data-id="element-767">
                  <IconPlus className="w-3.5 h-3.5" stroke={2.5} data-id="element-768" />
                  <span className="font-poppins text-xs font-medium" data-id="element-769">
                    Aggiungi
                  </span>
                </button>
              </div>

              <div className="space-y-3" data-id="element-770">
                {mockChildren.map(child => <button key={child.id} onClick={() => onOpenChild(child.id, child.name)} className="w-full bg-white rounded-2xl p-4 border border-yeppy-text border-opacity-10 flex items-center gap-4 text-left active:scale-[0.98] transition-transform" data-id="element-771">
                    <img src={child.avatar} alt={child.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0 border border-yeppy-text border-opacity-10" data-id="element-772" />
                    <div className="flex-1" data-id="element-773">
                      <h3 className="font-poppins font-medium text-sm text-yeppy-text" data-id="element-774">
                        {child.name}
                      </h3>
                      <p className="font-poppins text-sm text-yeppy-text text-opacity-60" data-id="element-775">
                        {child.age} anni · {child.infoCount} info salvate
                      </p>
                    </div>
                    <IconChevronRight className="w-5 h-5 text-yeppy-text text-opacity-30" stroke={1.5} data-id="element-776" />
                  </button>)}
              </div>
            </div>

            {/* Nucleo Familiare Section */}
            <div data-id="element-777">
              <div className="flex items-center justify-between mb-3" data-id="element-778">
                <h2 className="font-rubik font-semibold text-xs uppercase tracking-wider text-yeppy-text text-opacity-40" data-id="element-779">
                  Nucleo Familiare
                </h2>
                <button className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-yeppy-text border-opacity-15 text-yeppy-text text-opacity-60 active:scale-95 transition-transform" data-id="element-780">
                  <IconPlus className="w-3.5 h-3.5" stroke={2.5} data-id="element-781" />
                  <span className="font-poppins text-xs font-medium" data-id="element-782">
                    Aggiungi
                  </span>
                </button>
              </div>

              <div className="space-y-3" data-id="element-783">
                {caregiverCollaboration.map(caregiver => <button key={caregiver.id} onClick={() => onOpenCaregiver(caregiver.id, caregiver.name)} className="w-full bg-white rounded-2xl p-4 border border-yeppy-text border-opacity-10 flex items-center gap-3 text-left active:scale-[0.98] transition-transform" data-id="element-784">
                    <img src={caregiver.avatar} alt={caregiver.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0 border border-yeppy-text border-opacity-10" data-id="element-785" />
                    <div className="flex-1 min-w-0" data-id="element-786">
                      <div className="flex items-center justify-between mb-1" data-id="element-787">
                        <div className="flex items-center gap-1.5" data-id="element-788">
                          <span className="font-poppins text-sm font-medium text-yeppy-text" data-id="element-789">
                            {caregiver.name}
                          </span>
                          <span className="font-poppins text-xs text-yeppy-text text-opacity-40" data-id="element-790">
                            {caregiver.role}
                          </span>
                        </div>
                        <span className="font-rubik font-medium text-sm text-yeppy-text" data-id="element-791">
                          {caregiver.percentage}%
                        </span>
                      </div>
                      <div className="w-full h-1.5 bg-yeppy-bg rounded-full overflow-hidden" data-id="element-792">
                        <div className="h-full rounded-full transition-all duration-500" style={{
                    width: `${caregiver.percentage}%`,
                    backgroundColor: '#1D2A36'
                  }} data-id="element-793" />
                      </div>
                    </div>
                    <IconChevronRight className="w-4 h-4 text-yeppy-text text-opacity-30 flex-shrink-0" stroke={1.5} data-id="element-794" />
                  </button>)}
              </div>
            </div>
          </div>}
      </div>
    </div>;
}